import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Zap, 
  Package, 
  Gift, 
  Percent,
  Clock,
  Plus,
  Trash2,
  RefreshCw,
  Calendar,
  Tag,
  ShoppingCart,
} from 'lucide-react';
import { AdminLayout } from '../components/admin/AdminLayout';
import { NotificationBell } from '@/components/NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminPromotionsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("flash-sales");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [flashSales, setFlashSales] = useState([]);
  const [autoPromos, setAutoPromos] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [preOrders, setPreOrders] = useState([]);
  const [products, setProducts] = useState([]);
  
  // Form states
  const [showFlashForm, setShowFlashForm] = useState(false);
  const [showAutoForm, setShowAutoForm] = useState(false);
  const [showBundleForm, setShowBundleForm] = useState(false);
  const [showPreOrderForm, setShowPreOrderForm] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);

  const getToken = () => localStorage.getItem('admin_token');

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        navigate('/admin');
        return;
      }

      // Fetch flash sales
      const flashRes = await fetch(`${API_URL}/api/promotions/flash-sales?active_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (flashRes.ok) {
        const data = await flashRes.json();
        setFlashSales(data.flash_sales || []);
      }

      // Fetch auto promotions
      const autoRes = await fetch(`${API_URL}/api/promotions/auto`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (autoRes.ok) {
        const data = await autoRes.json();
        setAutoPromos(data.promotions || []);
      }

      // Fetch bundles
      const bundlesRes = await fetch(`${API_URL}/api/promotions/bundles`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (bundlesRes.ok) {
        const data = await bundlesRes.json();
        setBundles(data.bundles || []);
      }

      // Fetch gift cards
      const giftRes = await fetch(`${API_URL}/api/promotions/gift-cards`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (giftRes.ok) {
        const data = await giftRes.json();
        setGiftCards(data.gift_cards || []);
      }

      // Fetch pre-orders
      const preOrdersRes = await fetch(`${API_URL}/api/promotions/pre-orders`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (preOrdersRes.ok) {
        const data = await preOrdersRes.json();
        setPreOrders(data.pre_orders || []);
      }

      // Fetch products for selection
      const productsRes = await fetch(`${API_URL}/api/products?limit=100`);
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const deletePreOrder = async (id) => {
    if (!confirm('Supprimer cette pré-commande ?')) return;
    
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/promotions/pre-orders/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Pré-commande supprimée');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const deleteFlashSale = async (id) => {
    if (!confirm('Supprimer cette flash sale ?')) return;
    
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/promotions/flash-sales/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Flash sale supprimée');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleAutoPromo = async (id, isActive) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/promotions/auto/${id}?is_active=${!isActive}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(isActive ? 'Promotion désactivée' : 'Promotion activée');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteAutoPromo = async (id) => {
    if (!confirm('Supprimer cette promotion ?')) return;
    
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/promotions/auto/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Promotion supprimée');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return 'Terminé';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}j ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const deleteBundle = async (bundleId) => {
    if (!confirm('Supprimer ce bundle ?')) return;
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/promotions/bundles/${bundleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Bundle supprimé');
        fetchData();
      }
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <AdminLayout title="Promotions & Conversions" subtitle="Flash Sales, Bundles, Cartes Cadeaux">
      <div className="max-w-7xl mx-auto">
        {/* Header actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Percent className="w-6 h-6 text-[#C0000C]" />
            <h1 className="text-2xl font-bold text-white">Promotions & Conversions</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchData}
              className="border-zinc-700 text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <NotificationBell />
          </div>
        </div>

        <div className="space-y-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-6">
            <TabsTrigger value="flash-sales" className="data-[state=active]:bg-zinc-800">
              <Zap className="w-4 h-4 mr-2" />
              Flash Sales
            </TabsTrigger>
            <TabsTrigger value="auto" className="data-[state=active]:bg-zinc-800">
              <Tag className="w-4 h-4 mr-2" />
              Promotions Auto
            </TabsTrigger>
            <TabsTrigger value="bundles" className="data-[state=active]:bg-zinc-800">
              <Package className="w-4 h-4 mr-2" />
              Bundles
            </TabsTrigger>
            <TabsTrigger value="pre-orders" className="data-[state=active]:bg-zinc-800">
              <Clock className="w-4 h-4 mr-2" />
              Pré-commandes
            </TabsTrigger>
            <TabsTrigger value="gift-cards" className="data-[state=active]:bg-zinc-800">
              <Gift className="w-4 h-4 mr-2" />
              Cartes Cadeaux
            </TabsTrigger>
          </TabsList>

          {/* Flash Sales Tab */}
          <TabsContent value="flash-sales">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-white">Flash Sales avec compte à rebours</h2>
              <Button onClick={() => setShowFlashForm(!showFlashForm)} data-testid="add-flash-sale">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Flash Sale
              </Button>
            </div>

            {showFlashForm && (
              <FlashSaleForm 
                products={products} 
                onSuccess={() => { setShowFlashForm(false); fetchData(); }}
                onCancel={() => setShowFlashForm(false)}
              />
            )}

            <div className="grid gap-4">
              {flashSales.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-8 text-center">
                    <Zap className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-white">Aucune flash sale</p>
                  </CardContent>
                </Card>
              ) : (
                flashSales.map((fs) => (
                  <Card key={fs.id} className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                            <Zap className="w-6 h-6 text-red-500" />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">{fs.name?.fr || 'Flash Sale'}</h3>
                            <p className="text-sm text-white">
                              {fs.products?.length || 0} produits • -{fs.discount_percent}%
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <Badge className={fs.time_remaining > 0 ? "bg-green-500/20 text-green-400" : "bg-zinc-700"}>
                              {formatTimeRemaining(fs.time_remaining)}
                            </Badge>
                            <p className="text-xs text-gray-200 mt-1">
                              Fin: {formatDate(fs.end_date)}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteFlashSale(fs.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Products preview */}
                      {fs.products?.length > 0 && (
                        <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                          {fs.products.slice(0, 5).map((p) => (
                            <div key={p.id} className="flex-shrink-0 w-16">
                              <div className="w-16 h-16 bg-zinc-800 rounded overflow-hidden">
                                {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                              </div>
                              <p className="text-xs text-gray-200 mt-1 truncate">{p.flash_price}₪</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Auto Promotions Tab */}
          <TabsContent value="auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-white">Promotions automatiques (sans code)</h2>
              <Button onClick={() => setShowAutoForm(!showAutoForm)} data-testid="add-auto-promo">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Promo Auto
              </Button>
            </div>

            {showAutoForm && (
              <AutoPromoForm 
                onSuccess={() => { setShowAutoForm(false); fetchData(); }}
                onCancel={() => setShowAutoForm(false)}
              />
            )}

            <div className="grid gap-4">
              {autoPromos.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-8 text-center">
                    <Tag className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-white">Aucune promotion automatique</p>
                  </CardContent>
                </Card>
              ) : (
                autoPromos.map((promo) => (
                  <Card key={promo.id} className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${promo.is_active ? 'bg-green-500/20' : 'bg-zinc-700'}`}>
                            <Tag className={`w-5 h-5 ${promo.is_active ? 'text-green-500' : 'text-white'}`} />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">{promo.name?.fr || 'Promotion'}</h3>
                            <p className="text-sm text-white">
                              {promo.discount_type === 'cart_threshold' && `Seuil panier: ${promo.config?.threshold}₪ → -${promo.config?.discount_percent}%`}
                              {promo.discount_type === 'buy_x_get_y' && `${promo.config?.buy_quantity} achetés = ${promo.config?.get_quantity} offert`}
                              {promo.discount_type === 'category_discount' && `Catégorie ${promo.config?.category}: -${promo.config?.discount_percent}%`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Switch
                            checked={promo.is_active}
                            onCheckedChange={() => toggleAutoPromo(promo.id, promo.is_active)}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteAutoPromo(promo.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Bundles Tab */}
          <TabsContent value="bundles">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-white">Bundles / Packs</h2>
              <Button onClick={() => setShowBundleForm(!showBundleForm)} data-testid="add-bundle">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Bundle
              </Button>
            </div>

            {showBundleForm && (
              <BundleForm 
                products={products}
                onSuccess={() => { setShowBundleForm(false); fetchData(); }}
                onCancel={() => setShowBundleForm(false)}
              />
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {bundles.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800 md:col-span-2">
                  <CardContent className="p-8 text-center">
                    <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-white">Aucun bundle</p>
                  </CardContent>
                </Card>
              ) : (
                bundles.map((bundle) => (
                  <Card 
                    key={bundle.id} 
                    className="bg-zinc-900/50 border-zinc-800 cursor-pointer hover:border-zinc-600 transition-colors"
                    onClick={() => setSelectedBundle(bundle)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-white font-medium">{bundle.name?.fr || 'Bundle'}</h3>
                          <p className="text-sm text-white">{bundle.products?.length || 0} produits</p>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400">
                          -{bundle.savings_percent}%
                        </Badge>
                      </div>
                      <div className="flex gap-2 mb-3 flex-wrap">
                        {bundle.products?.slice(0, 4).map((p) => (
                          <div key={p.id} className="w-16 h-16 bg-zinc-800 rounded overflow-hidden">
                            {p.image && <img src={p.image.startsWith('/') ? `${API_URL}${p.image}` : p.image} alt="" className="w-full h-full object-cover" />}
                          </div>
                        ))}
                        {bundle.products?.length > 4 && (
                          <div className="w-16 h-16 bg-zinc-800 rounded flex items-center justify-center text-gray-400">
                            +{bundle.products.length - 4}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-gray-200 line-through text-sm">{bundle.original_price}₪</span>
                          <span className="text-white font-bold ml-2">{bundle.bundle_price}₪</span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-400"
                          onClick={(e) => { e.stopPropagation(); deleteBundle(bundle.id); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Pre-Orders Tab */}
          <TabsContent value="pre-orders">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-white">Pré-commandes</h2>
              <Button onClick={() => setShowPreOrderForm(!showPreOrderForm)} data-testid="add-pre-order">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Pré-commande
              </Button>
            </div>

            {showPreOrderForm && (
              <PreOrderForm 
                products={products}
                onSuccess={() => { setShowPreOrderForm(false); fetchData(); }}
                onCancel={() => setShowPreOrderForm(false)}
              />
            )}

            <div className="grid gap-4">
              {preOrders.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-8 text-center">
                    <Clock className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-white">Aucune pré-commande configurée</p>
                    <p className="text-sm text-gray-200 mt-2">
                      Activez les pré-commandes pour vos produits à venir
                    </p>
                  </CardContent>
                </Card>
              ) : (
                preOrders.map((po) => (
                  <Card key={po.id} className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-zinc-800 rounded overflow-hidden">
                            {po.product?.image && (
                              <img src={po.product.image} alt="" className="w-full h-full object-cover" />
                            )}
                          </div>
                          <div>
                            <h3 className="text-white font-medium">{po.product?.name?.fr || 'Produit'}</h3>
                            <p className="text-sm text-white">
                              Disponible le {formatDate(po.available_date)}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className="bg-blue-500/20 text-blue-400">
                                {po.remaining_quantity} places restantes
                              </Badge>
                              {po.deposit_percent > 0 && (
                                <Badge className="bg-orange-500/20 text-orange-400">
                                  Acompte {po.deposit_percent}%
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-lg font-bold text-white">
                              J-{po.days_until_available || 0}
                            </p>
                            <p className="text-xs text-gray-200">
                              {po.reserved_quantity || 0} réservés
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deletePreOrder(po.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Gift Cards Tab */}
          <TabsContent value="gift-cards">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-white">Cartes Cadeaux</h2>
            </div>

            <div className="grid gap-4">
              {giftCards.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-8 text-center">
                    <Gift className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-white">Aucune carte cadeau émise</p>
                  </CardContent>
                </Card>
              ) : (
                giftCards.map((card) => (
                  <Card key={card.id} className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${card.is_active ? 'bg-purple-500/20' : 'bg-zinc-700'}`}>
                            <Gift className={`w-5 h-5 ${card.is_active ? 'text-purple-500' : 'text-white'}`} />
                          </div>
                          <div>
                            <p className="text-white font-mono">{card.code}</p>
                            <p className="text-sm text-white">
                              {card.recipient_email ? `Pour: ${card.recipient_email}` : 'Non attribué'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-bold">{card.remaining_amount}₪ / {card.initial_amount}₪</p>
                          <Badge className={card.is_active ? "bg-green-500/20 text-green-400" : "bg-zinc-700"}>
                            {card.is_active ? 'Active' : 'Utilisée'}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
        </div>
      </div>

      {/* Bundle Detail Modal */}
      <Dialog open={!!selectedBundle} onOpenChange={() => setSelectedBundle(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {selectedBundle?.name?.fr || 'Bundle'}
            </DialogTitle>
            <DialogDescription>
              {selectedBundle?.products?.length || 0} produits • Économie de {selectedBundle?.savings_percent}%
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Prix */}
            <div className="flex items-center gap-4 p-4 bg-zinc-800/50 rounded-lg">
              <div>
                <p className="text-sm text-gray-400">Prix original</p>
                <p className="text-xl text-gray-400 line-through">{selectedBundle?.original_price}₪</p>
              </div>
              <div className="text-2xl text-gray-500">→</div>
              <div>
                <p className="text-sm text-green-400">Prix bundle</p>
                <p className="text-2xl text-green-400 font-bold">{selectedBundle?.bundle_price}₪</p>
              </div>
              <Badge className="ml-auto bg-green-500/20 text-green-400 text-lg px-3 py-1">
                -{selectedBundle?.savings_percent}%
              </Badge>
            </div>

            {/* Liste des produits avec grandes images */}
            <div>
              <h3 className="text-white font-medium mb-4">Produits du bundle</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {selectedBundle?.products?.map((p) => (
                  <div key={p.id} className="bg-zinc-800 rounded-lg overflow-hidden">
                    <div className="aspect-square bg-zinc-700">
                      {p.image && (
                        <img 
                          src={p.image.startsWith('/') ? `${API_URL}${p.image}` : p.image} 
                          alt={typeof p.name === 'object' ? (p.name?.fr || '') : p.name} 
                          className="w-full h-full object-cover" 
                        />
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-white text-sm font-medium line-clamp-2">
                        {typeof p.name === 'object' ? (p.name?.fr || p.name?.en || 'Produit') : p.name}
                      </p>
                      <p className="text-gray-400 text-sm mt-1">{p.price}₪</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedBundle(null)}>
              Fermer
            </Button>
            <Button 
              variant="destructive"
              onClick={() => { deleteBundle(selectedBundle?.id); setSelectedBundle(null); }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

// Flash Sale Form Component
function FlashSaleForm({ products, onSuccess, onCancel }) {
  const [name, setName] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [discountPercent, setDiscountPercent] = useState(20);
  const [endDate, setEndDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filtrer les produits par recherche
  const filteredProducts = products.filter(p => {
    const productName = p.name?.fr || p.title || '';
    return !searchQuery || 
      productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const toggleProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || selectedProducts.length === 0 || !endDate) {
      toast.error('Remplissez tous les champs');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/promotions/flash-sales`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: { fr: name, en: name },
          product_ids: selectedProducts,
          discount_percent: discountPercent,
          start_date: new Date().toISOString(),
          end_date: new Date(endDate).toISOString(),
        }),
      });

      if (res.ok) {
        toast.success('Flash sale créée !');
        onSuccess();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
      <CardHeader>
        <CardTitle className="text-white">Nouvelle Flash Sale</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Nom de la vente</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Soldes de folie"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Réduction (%)</Label>
              <Input
                type="number"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(parseInt(e.target.value))}
                min={1}
                max={90}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>

          <div>
            <Label>Date de fin</Label>
            <Input
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-zinc-800 border-zinc-700"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Produits ({selectedProducts.length} sélectionné(s))</Label>
              {selectedProducts.length > 0 && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedProducts([])}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  Tout désélectionner
                </Button>
              )}
            </div>
            
            {/* Barre de recherche */}
            <div className="relative mb-3">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un produit..."
                className="bg-zinc-800 border-zinc-700 pl-10"
              />
              <ShoppingCart className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            
            {/* Zone de sélection des produits - Plus grande */}
            <div className="border border-zinc-700 rounded-lg p-3 max-h-80 overflow-y-auto bg-zinc-800/30">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>{searchQuery ? 'Aucun produit trouvé' : 'Chargement des produits...'}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {filteredProducts.map((p) => {
                    const isSelected = selectedProducts.includes(p.id);
                    // Gérer la structure images: {main: [...], qc: [...], variants: [...]}
                    let imgUrl = null;
                    if (p.images) {
                      if (Array.isArray(p.images)) {
                        imgUrl = p.images[0];
                      } else if (p.images.main && p.images.main.length > 0) {
                        imgUrl = p.images.main[0];
                      }
                    }
                    const fullImgUrl = imgUrl?.startsWith('/api/') ? `${API_URL}${imgUrl}` : imgUrl;
                    const productName = p.name?.fr || p.title || 'Sans nom';
                    const productPrice = p.price || p.pricing?.final_price || 0;
                    
                    return (
                      <div
                        key={p.id}
                        onClick={() => toggleProduct(p.id)}
                        className={`cursor-pointer rounded-lg border-2 transition-all overflow-hidden ${
                          isSelected 
                            ? 'border-primary bg-primary/10 ring-2 ring-primary/30' 
                            : 'border-zinc-700 hover:border-zinc-500'
                        }`}
                      >
                        <div className="aspect-square bg-zinc-900 overflow-hidden">
                          {fullImgUrl ? (
                            <img 
                              src={fullImgUrl} 
                              alt={productName} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-8 h-8 text-zinc-600" />
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-xs text-white truncate font-medium">{productName}</p>
                          <p className="text-xs text-gray-400">{Number(productPrice).toFixed(2)} ₪</p>
                        </div>
                        {isSelected && (
                          <div className="bg-primary text-white text-xs py-1 text-center">
                            Sélectionné
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving}>
              {saving ? 'Création...' : 'Créer la Flash Sale'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Annuler
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// Auto Promo Form Component
function AutoPromoForm({ onSuccess, onCancel }) {
  const [name, setName] = useState('');
  const [discountType, setDiscountType] = useState('cart_threshold');
  const [threshold, setThreshold] = useState(100);
  const [discountPercent, setDiscountPercent] = useState(10);
  const [buyQty, setBuyQty] = useState(2);
  const [getQty, setGetQty] = useState(1);
  const [category, setCategory] = useState('sneakers');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name) {
      toast.error('Entrez un nom');
      return;
    }

    setSaving(true);
    try {
      let config = {};
      if (discountType === 'cart_threshold') {
        config = { threshold, discount_percent: discountPercent };
      } else if (discountType === 'buy_x_get_y') {
        config = { buy_quantity: buyQty, get_quantity: getQty, get_discount_percent: 100 };
      } else if (discountType === 'category_discount') {
        config = { category, discount_percent: discountPercent };
      }

      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/promotions/auto`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: { fr: name, en: name },
          discount_type: discountType,
          config,
        }),
      });

      if (res.ok) {
        toast.success('Promotion créée !');
        onSuccess();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
      <CardHeader>
        <CardTitle className="text-white">Nouvelle Promotion Automatique</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Nom</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: -10% dès 100₪"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Type de promotion</Label>
              <Select value={discountType} onValueChange={setDiscountType}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cart_threshold">Seuil panier (ex: -10% dès 100₪)</SelectItem>
                  <SelectItem value="buy_x_get_y">X achetés = Y offert</SelectItem>
                  <SelectItem value="category_discount">Réduction catégorie</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {discountType === 'cart_threshold' && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Seuil minimum (₪)</Label>
                <Input
                  type="number"
                  value={threshold}
                  onChange={(e) => setThreshold(parseInt(e.target.value))}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label>Réduction (%)</Label>
                <Input
                  type="number"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(parseInt(e.target.value))}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
          )}

          {discountType === 'buy_x_get_y' && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Quantité à acheter</Label>
                <Input
                  type="number"
                  value={buyQty}
                  onChange={(e) => setBuyQty(parseInt(e.target.value))}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div>
                <Label>Quantité offerte</Label>
                <Input
                  type="number"
                  value={getQty}
                  onChange={(e) => setGetQty(parseInt(e.target.value))}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
          )}

          {discountType === 'category_discount' && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Catégorie</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sneakers">Sneakers</SelectItem>
                    <SelectItem value="hoodies">Hoodies</SelectItem>
                    <SelectItem value="tshirts">T-Shirts</SelectItem>
                    <SelectItem value="accessories">Accessoires</SelectItem>
                    <SelectItem value="jackets">Vestes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Réduction (%)</Label>
                <Input
                  type="number"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(parseInt(e.target.value))}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              {saving ? 'Création...' : 'Créer la Promotion'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Annuler
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// Bundle Form Component
function BundleForm({ products, onSuccess, onCancel }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bundlePrice, setBundlePrice] = useState(0);
  const [saving, setSaving] = useState(false);

  const originalPrice = selectedProducts.reduce((sum, pid) => {
    const product = products.find(p => p.id === pid);
    return sum + (product?.price?.amount || 0);
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || selectedProducts.length < 2 || bundlePrice <= 0) {
      toast.error('Remplissez tous les champs (min 2 produits)');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/promotions/bundles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: { fr: name, en: name },
          description: { fr: description, en: description },
          product_ids: selectedProducts,
          bundle_price: bundlePrice,
          original_price: originalPrice,
        }),
      });

      if (res.ok) {
        toast.success('Bundle créé !');
        onSuccess();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
      <CardHeader>
        <CardTitle className="text-white">Nouveau Bundle</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Nom du bundle</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Pack Streetwear Complet"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Prix du bundle (₪)</Label>
              <Input
                type="number"
                value={bundlePrice}
                onChange={(e) => setBundlePrice(parseFloat(e.target.value))}
                className="bg-zinc-800 border-zinc-700"
              />
              {originalPrice > 0 && (
                <p className="text-xs text-white mt-1">
                  Prix original: {originalPrice}₪ → Économie: {originalPrice - bundlePrice}₪
                </p>
              )}
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description du bundle"
              className="bg-zinc-800 border-zinc-700"
            />
          </div>

          <div>
            <Label>Produits du bundle ({selectedProducts.length} sélectionné(s))</Label>
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3 mt-2 max-h-[500px] overflow-y-auto p-2">
              {products.map((p) => {
                // Récupérer la première image (différents formats possibles)
                let imageUrl = null;
                if (p.images?.main?.[0]) {
                  imageUrl = p.images.main[0];
                } else if (Array.isArray(p.images) && p.images[0]) {
                  imageUrl = p.images[0];
                }
                // Préfixer avec API_URL si c'est un chemin relatif
                if (imageUrl && imageUrl.startsWith('/')) {
                  imageUrl = `${API_URL}${imageUrl}`;
                }
                
                const productName = typeof p.name === 'object' ? (p.name?.fr || p.name?.en || '') : p.name;
                
                return (
                  <div
                    key={p.id}
                    onClick={() => {
                      if (selectedProducts.includes(p.id)) {
                        setSelectedProducts(selectedProducts.filter(id => id !== p.id));
                      } else {
                        setSelectedProducts([...selectedProducts, p.id]);
                      }
                    }}
                    className={`cursor-pointer rounded-lg border-2 transition-all hover:scale-105 ${
                      selectedProducts.includes(p.id) 
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/50' 
                        : 'border-zinc-700 hover:border-zinc-500'
                    }`}
                  >
                    <div className="aspect-square bg-zinc-800 rounded-t-lg overflow-hidden">
                      {imageUrl ? (
                        <img src={imageUrl} alt={productName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
                          No image
                        </div>
                      )}
                    </div>
                    <div className="p-2 bg-zinc-800/50 rounded-b-lg">
                      <p className="text-xs text-white truncate font-medium">{productName?.substring(0, 20)}</p>
                      <p className="text-xs text-green-400">{p.price?.amount?.toFixed(0) || 0}₪</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              {saving ? 'Création...' : 'Créer le Bundle'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Annuler
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// Pre-Order Form Component
function PreOrderForm({ products, onSuccess, onCancel }) {
  const [selectedProduct, setSelectedProduct] = useState('');
  const [availableDate, setAvailableDate] = useState('');
  const [maxQuantity, setMaxQuantity] = useState(50);
  const [depositPercent, setDepositPercent] = useState(0);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProduct || !availableDate) {
      toast.error('Sélectionnez un produit et une date');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/promotions/pre-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: selectedProduct,
          available_date: new Date(availableDate).toISOString(),
          max_quantity: maxQuantity,
          deposit_percent: depositPercent,
        }),
      });

      if (res.ok) {
        toast.success('Pré-commande créée !');
        onSuccess();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
      <CardHeader>
        <CardTitle className="text-white">Nouvelle Pré-commande</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Produit</Label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Sélectionner un produit" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name?.fr || p.slug}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date de disponibilité</Label>
              <Input
                type="date"
                value={availableDate}
                onChange={(e) => setAvailableDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Quantité maximum</Label>
              <Input
                type="number"
                value={maxQuantity}
                onChange={(e) => setMaxQuantity(parseInt(e.target.value))}
                min={1}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Acompte (%) — 0 = paiement complet</Label>
              <Input
                type="number"
                value={depositPercent}
                onChange={(e) => setDepositPercent(parseInt(e.target.value))}
                min={0}
                max={100}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              {saving ? 'Création...' : 'Créer la Pré-commande'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Annuler
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default AdminPromotionsPage;
